import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { loginRequest } from "./msalConfig";

// Declare MSAL global for TypeScript
declare global {
  interface Window {
    msal: any; // Using any here as we don't need the full MSAL type
  }
}

// Get auth token from MSAL
export const getAuthToken = async (): Promise<string | null> => {
  try {
    // If running in browser
    if (typeof window !== 'undefined' && window.msal) {
      const accounts = window.msal.getAllAccounts();
      
      if (accounts.length === 0) {
        console.warn('[AUTH] No accounts found, returning null token');
        return null;
      }
      
      const result = await window.msal.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      
      return result.accessToken;
    }
    return null; // Return null if window or msal is not defined
  } catch (err) {
    console.error("[AUTH] Failed to get token:", err);
    return null;
  }
};

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  customHeaders?: HeadersInit
): Promise<Response> {
  // Only get in-memory preference from session storage or localStorage
  const useInMemory = 
    sessionStorage.getItem('dataverse_use_in_memory') === 'true' || 
    localStorage.getItem('dataverse_use_in_memory') === 'true';
  
  // Build headers with API configuration
  const headers: Record<string, string> = {
    ...(data ? { "Content-Type": "application/json" } : {}),
    ...(customHeaders as Record<string, string> || {})
  };

  // Only pass in-memory preference if it's true
  if (useInMemory) {
    headers['x-use-in-memory'] = 'true';
  }
  
  // Add auth token if available
  const token = await getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log(`[AUTH] Added token to ${url}`);
  } else {
    console.warn(`[AUTH] No token for ${url}`);
  }

  // Determine if we should use a custom API base URL
  const apiBaseUrl = localStorage.getItem('dataverse_api_url');
  let fullUrl = url;

  // If we have a custom API URL and this is an API request, use the custom URL
  if (apiBaseUrl && url.startsWith('/api/')) {
    // Remove the /api prefix as the Azure Functions routes don't have it
    const apiPath = url.substring(4); // Remove '/api'
    fullUrl = `${apiBaseUrl}${apiPath}`;
    console.log(`[API] Using custom API URL: ${fullUrl}`);
  }

  //console.log(`[DEBUG] Request headers for ${fullUrl}:`, headers);

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  // Log response details
  console.log(`[DEBUG] Response status for ${fullUrl}:`, res.status, res.statusText);
  
  // Build an object of response headers for logging
  const headerObj: Record<string, string> = {};
  res.headers.forEach((value, key) => {
    headerObj[key] = value;
  });
  console.log(`[DEBUG] Response headers for ${fullUrl}:`, headerObj);
  
  await throwIfResNotOk(res);
  return res;
}

async function throwIfResNotOk(res: Response): Promise<void> {
  if (!res.ok) {
    let errorMessage = `HTTP error! status: ${res.status}`;
    try {
      const errorData = await res.clone().json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch (_e) {
      // If response cannot be parsed as JSON, use text
      try {
        const errorText = await res.clone().text();
        if (errorText) {
          errorMessage = errorText;
        }
      } catch (_e2) {
        // If all else fails, use the original error message
      }
    }
    throw new Error(errorMessage);
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Only get in-memory preference from session storage or localStorage
    const useInMemory = 
      sessionStorage.getItem('dataverse_use_in_memory') === 'true' || 
      localStorage.getItem('dataverse_use_in_memory') === 'true';
    
    // Build headers with API configuration
    const headers: Record<string, string> = {};
    
    // Only pass in-memory preference if it's true
    if (useInMemory) {
      headers['x-use-in-memory'] = 'true';
    }
    
    // Add auth token if available
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log(`[AUTH] Added token to query ${queryKey[0]}`);
    } else {
      console.log(`[DEBUG-QUERY] No token for query ${queryKey[0]}`);
    }
    
    // Convert queryKey to URL and params
    const [url, params] = queryKey;
    const queryParams = new URLSearchParams(params as Record<string, string>);
    
    console.log(`[DEBUG-QUERY] Full request URL: ${url}?${queryParams}`);
    console.log('[DEBUG-QUERY] Headers:', headers);

    // Determine if we should use a custom API base URL
    const apiBaseUrl = localStorage.getItem('dataverse_api_url');
    let fullUrl = url as string;

    // If we have a custom API URL and this is an API request, use the custom URL
    if (apiBaseUrl && (url as string).startsWith('/api/')) {
      // Remove the /api prefix as the Azure Functions routes don't have it
      const apiPath = (url as string).substring(4); // Remove '/api'
      fullUrl = `${apiBaseUrl}${apiPath}`;
      console.log(`[API] Using custom API URL for query: ${fullUrl}`);
    }

    const response = await fetch(`${fullUrl}?${queryParams}`, {
      credentials: "include",
      headers
    });

    console.log(`[DEBUG-QUERY] Response status for ${url}: ${response.status} ${response.statusText}`);

    if (unauthorizedBehavior === "returnNull" && response.status === 401) {
      return null;
    }

    await throwIfResNotOk(response);
    return await response.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: async ({ queryKey }) => {
        console.log('Query Key:', queryKey);
        const [url, params] = queryKey;
        const queryParams = new URLSearchParams(params as Record<string, string>);

        // Get token for authorization
        const token = await getAuthToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log(`[DEBUG-QUERY] Using token for ${url}: ${token.substring(0, 15)}...`);
        } else {
          console.log(`[DEBUG-QUERY] No token for query ${url}`);
        }

        // Determine if we should use a custom API base URL
        const apiBaseUrl = localStorage.getItem('dataverse_api_url');
        let fullUrl = url as string;

        // If we have a custom API URL and this is an API request, use the custom URL
        if (apiBaseUrl && (url as string).startsWith('/api/')) {
          // Remove the /api prefix as the Azure Functions routes don't have it
          const apiPath = (url as string).substring(4); // Remove '/api'
          fullUrl = `${apiBaseUrl}${apiPath}`;
          console.log(`[API] Using custom API URL for query client: ${fullUrl}`);
        }

        console.log(`[DEBUG-QUERY] Full request URL: ${fullUrl}?${queryParams}`);
        console.log('[DEBUG-QUERY] Headers:', headers);

        const response = await fetch(`${fullUrl}?${queryParams}`, {
          credentials: "include",
          headers
        });

        console.log(`[DEBUG-QUERY] Response status for ${url}: ${response.status} ${response.statusText}`);

        // Handle auth errors by redirecting to login
        if (response.status === 401 || response.status === 403) {
          console.warn(`Auth error ${response.status} when calling API: ${url}`);
          // Redirect to login after a short delay
          setTimeout(() => {
            window.location.href = '/.auth/login/aad';
          }, 100);
          throw new Error(`Authentication error: ${response.status}`);
        }

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }

        return response.json();
      },
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5000, // Cache data for 5 seconds
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (error instanceof Error && error.message.includes('Authentication error')) {
          return false;
        }
        // Don't retry more than 3 times
        if (failureCount >= 3) {
          return false;
        }
        // Retry on network errors
        return true;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 1s, 2s, 4s, 8s
        return Math.min(1000 * 2 ** attemptIndex, 30000);
      },
    },
    mutations: {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries();
      },
    },
  }
});
